"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { getSmsHistoryForPhone } from "@/app/(authenticated)/communication/_modules/services/smsHistoryService";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { removeRealtimeChannel } from "../services/realtimeTeardown";

interface SmsMessage {
  id: string;
  text: string;
  direction: "inbound" | "outbound";
  timestamp: Date;
  // Full carrier status spectrum so the widget bubble can color-code in/out
  // of every state without remapping at the boundary.
  status?: "sending" | "queued" | "sent" | "delivered" | "failed" | "undelivered";
  messageSid?: string; // message SID for matching status updates
  mediaUrls?: string[]; // MMS media attachments
  errorCode?: string | null; // carrier error code on a failed message
  errorMessage?: string | null; // raw error description on a failed message
}

interface ActiveSmsConversation {
  leadId: string;
  leadName: string;
  phoneNumber: string;
  messages: SmsMessage[];
  lastMessageAt: Date;
}

interface SmsConversationContextValue {
  activeSmsConversation: ActiveSmsConversation | null;
  isMinimized: boolean;
  startConversation: (params: { leadId: string; leadName: string; phoneNumber: string; initialMessage?: string }) => Promise<void>;
  addMessage: (message: Omit<SmsMessage, "id">) => void;
  closeConversation: () => void;
  minimizeConversation: () => void;
  maximizeConversation: () => void;
  refreshMessages: () => Promise<void>;
}

const SmsConversationContext = createContext<SmsConversationContextValue | undefined>(undefined);

interface SmsConversationProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY_CONVERSATION = 'sms_active_conversation';
const STORAGE_KEY_MINIMIZED = 'sms_is_minimized';

// Polling interval for SMS refresh — fallback only.
// Primary delivery is via Supabase Realtime broadcast (incoming_sms:{userId}).
const SMS_POLL_INTERVAL = 60000;

export const SmsConversationProvider: React.FC<SmsConversationProviderProps> = ({ children }) => {
  const [activeSmsConversation, setActiveSmsConversation] = useState<ActiveSmsConversation | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const { user } = useUserContext();
  const userId = user?.user?.id;

  // Refs for real-time and polling
  const supabaseChannelRef = useRef<any>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const conversationRef = useRef<ActiveSmsConversation | null>(null);

  // Keep ref in sync with state for use in callbacks
  useEffect(() => {
    conversationRef.current = activeSmsConversation;
  }, [activeSmsConversation]);

  // Load conversation from sessionStorage on mount
  useEffect(() => {
    try {
      const storedConversation = sessionStorage.getItem(STORAGE_KEY_CONVERSATION);
      const storedMinimized = sessionStorage.getItem(STORAGE_KEY_MINIMIZED);

      if (storedConversation) {
        const parsed = JSON.parse(storedConversation);
        // Convert timestamp strings back to Date objects
        parsed.lastMessageAt = new Date(parsed.lastMessageAt);
        parsed.messages = parsed.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
        setActiveSmsConversation(parsed);
      }

      if (storedMinimized) {
        setIsMinimized(JSON.parse(storedMinimized));
      }
    } catch (error) {
      // Clear invalid data
      sessionStorage.removeItem(STORAGE_KEY_CONVERSATION);
      sessionStorage.removeItem(STORAGE_KEY_MINIMIZED);
    }
  }, []);

  // Save conversation to sessionStorage whenever it changes
  useEffect(() => {
    try {
      if (activeSmsConversation) {
        sessionStorage.setItem(STORAGE_KEY_CONVERSATION, JSON.stringify(activeSmsConversation));
      } else {
        sessionStorage.removeItem(STORAGE_KEY_CONVERSATION);
      }
    } catch (error) {
    }
  }, [activeSmsConversation]);

  // Save minimized state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY_MINIMIZED, JSON.stringify(isMinimized));
    } catch (error) {
    }
  }, [isMinimized]);

  // Real-time SMS updates via Supabase broadcast
  useEffect(() => {
    if (!userId) return;

    const setupRealtimeListener = async () => {
      try {
        const supabase = await getSupabaseClient();
        const channelName = `incoming_sms:${userId}`;

        // Subscribe to incoming SMS events for this user
        const channel = supabase
          .channel(channelName)
          .on('broadcast', { event: 'new_sms' }, (payload: any) => {
            const { phoneNumber, messageContent, direction, timestamp, mediaUrls, media_urls } = payload.payload || payload;

            // Check if this SMS is for the currently open conversation
            const currentConv = conversationRef.current;

            if (currentConv) {
              // Normalize phone numbers for comparison (last 10 digits)
              const normalizePhone = (phone: string) => phone.replace(/\D/g, '').slice(-10);
              const incomingPhone = normalizePhone(phoneNumber || '');
              const conversationPhone = normalizePhone(currentConv.phoneNumber || '');

              if (incomingPhone === conversationPhone) {
                // Add the new message to the conversation
                const newMessage: SmsMessage = {
                  id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  text: messageContent,
                  direction: direction === 'inbound' ? 'inbound' : 'outbound',
                  timestamp: new Date(timestamp || Date.now()),
                  status: 'delivered',
                  mediaUrls: mediaUrls || media_urls || undefined, // MMS attachments
                };

                setActiveSmsConversation(prev => {
                  if (!prev) return null;
                  // Check if message already exists (dedupe)
                  const exists = prev.messages.some(
                    m => m.text === newMessage.text &&
                         Math.abs(m.timestamp.getTime() - newMessage.timestamp.getTime()) < 5000
                  );
                  if (exists) {
                    return prev;
                  }

                  return {
                    ...prev,
                    messages: [...prev.messages, newMessage],
                    lastMessageAt: new Date(),
                  };
                });
              }
            }
          })
          .subscribe(() => {
            // Channel subscription successful
          });

        // Separate channel for outbound status lifecycle updates (sent →
        // delivered / undelivered / failed). Posted by the backend after the
        // carrier fires its status webhook. Match against messageSid. A failure
        // also carries errorCode/errorMessage so the bubble can show the reason.
        const statusChannelName = `sms_status:${userId}`;
        const statusChannel = supabase
          .channel(statusChannelName)
          .on('broadcast', { event: 'status_update' }, (payload: any) => {
            const { messageSid, messageStatus, errorCode, errorMessage } = payload.payload || payload;
            if (!messageSid || !messageStatus) return;

            setActiveSmsConversation(prev => {
              if (!prev) return null;
              const next = prev.messages.map(m =>
                m.messageSid === messageSid
                  ? {
                      ...m,
                      status: messageStatus as SmsMessage['status'],
                      errorCode: errorCode ?? null,
                      errorMessage: errorMessage ?? null,
                    }
                  : m
              );
              // No matching message in the open conversation → ignore
              const changed = next.some((m, i) => m.status !== prev.messages[i].status);
              if (!changed) return prev;
              return { ...prev, messages: next };
            });
          })
          .subscribe(() => {
            // Status channel subscribed
          });

        supabaseChannelRef.current = { incoming: channel, status: statusChannel };
      } catch (error) {
        console.error('[SmsConversation] Error setting up realtime listener:', error);
        // Silently fail - polling will handle updates
      }
    };

    setupRealtimeListener();

    // Cleanup on unmount
    return () => {
      if (supabaseChannelRef.current) {
        // Old shape was a single channel; new shape is { incoming, status }.
        // Handle both for safety.
        const ref = supabaseChannelRef.current;
        if (ref?.incoming) removeRealtimeChannel(ref.incoming);
        if (ref?.status) removeRealtimeChannel(ref.status);
        if (typeof ref.unsubscribe === 'function') removeRealtimeChannel(ref as any);
        supabaseChannelRef.current = null;
      }
    };
  }, [userId]);

  // Polling fallback for SMS updates when conversation is open
  useEffect(() => {
    // Only poll when there's an active conversation and it's visible
    if (!activeSmsConversation || isMinimized) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }


    const pollForUpdates = async () => {
      const currentConv = conversationRef.current;
      if (!currentConv) return;

      try {
        const historyMessages = await getSmsHistoryForPhone(currentConv.phoneNumber);

        // Only update if we have new messages
        setActiveSmsConversation(prev => {
          if (!prev) return null;

          // Check if there are new messages by comparing counts and last message
          if (historyMessages.length > prev.messages.length ||
              (historyMessages.length > 0 && prev.messages.length > 0 &&
               historyMessages[historyMessages.length - 1].text !== prev.messages[prev.messages.length - 1].text)) {
            return {
              ...prev,
              messages: historyMessages,
              lastMessageAt: new Date(),
            };
          }
          return prev;
        });
      } catch (error) {
        console.error('[SmsConversation] Polling error:', error);
        // Silently fail polling
      }
    };

    // Poll immediately on mount
    pollForUpdates();

    // Start polling
    pollingIntervalRef.current = setInterval(pollForUpdates, SMS_POLL_INTERVAL);

    // Cleanup on unmount or when conversation changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [activeSmsConversation?.phoneNumber, isMinimized]);

  const startConversation = useCallback(async (params: { leadId: string; leadName: string; phoneNumber: string; initialMessage?: string }) => {

    const { leadId, leadName, phoneNumber, initialMessage } = params;

    // First, load SMS history from backend
    const historyMessages = await getSmsHistoryForPhone(phoneNumber);

    let messages: SmsMessage[] = [...historyMessages];

    // If there's an initial message (just sent), add it to the end if not already in history
    if (initialMessage) {
      const recentMessageExists = messages.some(
        msg => msg.text === initialMessage &&
               msg.direction === 'outbound' &&
               (Date.now() - msg.timestamp.getTime()) < 5000 // Within last 5 seconds
      );

      if (!recentMessageExists) {
        messages.push({
          id: `msg-${Date.now()}`,
          text: initialMessage,
          direction: "outbound",
          timestamp: new Date(),
          status: "sent",
        });
      }
    }

    const conversation = {
      leadId,
      leadName,
      phoneNumber,
      messages,
      lastMessageAt: new Date(),
    };

    setActiveSmsConversation(conversation);

    // Show the widget (not minimized)
    setIsMinimized(false);
  }, []);

  const addMessage = useCallback((message: Omit<SmsMessage, "id">) => {
    if (!activeSmsConversation) return;

    const newMessage: SmsMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    setActiveSmsConversation({
      ...activeSmsConversation,
      messages: [...activeSmsConversation.messages, newMessage],
      lastMessageAt: new Date(),
    });
  }, [activeSmsConversation]);

  const closeConversation = useCallback(() => {
    setActiveSmsConversation(null);
    setIsMinimized(false);
    // Storage cleanup is handled by the useEffect
  }, []);

  const minimizeConversation = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeConversation = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // Refresh messages from backend (for real-time updates)
  const refreshMessages = useCallback(async () => {
    if (!activeSmsConversation) return;

    const historyMessages = await getSmsHistoryForPhone(activeSmsConversation.phoneNumber);

    setActiveSmsConversation(prev => {
      if (!prev) return null;
      return {
        ...prev,
        messages: historyMessages,
        lastMessageAt: new Date(),
      };
    });
  }, [activeSmsConversation]);

  return (
    <SmsConversationContext.Provider
      value={{
        activeSmsConversation,
        isMinimized,
        startConversation,
        addMessage,
        closeConversation,
        minimizeConversation,
        maximizeConversation,
        refreshMessages,
      }}
    >
      {children}
    </SmsConversationContext.Provider>
  );
};

export const useSmsConversation = () => {
  const context = useContext(SmsConversationContext);
  if (context === undefined) {
    throw new Error("useSmsConversation must be used within a SmsConversationProvider");
  }
  return context;
};
